import PropTypes from 'prop-types';
import React, { useState } from 'react';

import {
  PERSONAL_LOAN,
  VEHICLE_LOAN,
  MORTGAGE_LOAN,
  STUDENT_LOAN,
} from '../../constants/liabilityTypes';
import {
  MAX_AMOUNT_LIMIT,
  MAX_INTEREST_RATE_LIMIT,
  MIN_AMOUNT_LIMIT,
  MIN_INTEREST_RATE_LIMIT,
} from '../../constants/numberLimit';
import { updateValue } from '../../utils/utils';
import Button from '../Button';
import CombinedTextField from '../CombinedTextField';
import InputField from '../InputField';
import Select from '../Select';
import './index.scss';

const LoanInputCard = ({ addLoan, cancelLoanInput, currencies, defaultCurrency }) => {
  const [liabilityType, setLiabilityType] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency);
  const [amount, setAmount] = useState('');
  const [rate, setRate] = useState('');
  const [isClicked, setIsClicked] = useState(false);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [lastRepaymentMonth, setLastRepaymentMonth] = useState('');
  const [lastRepaymentYear, setLastRepaymentYear] = useState('');

  const months = [{ label: 'Month', value: '' }].concat(
    Array.from({ length: 12 }, (_, i) => ({
      label: new Date(2000, i).toLocaleString('default', { month: 'long' }),
      value: (i + 1).toString(),
    })),
  );
  const years = [{ label: 'Year', value: '' }].concat(
    Array.from({ length: 30 }, (_, i) => ({
      label: (currentYear + i).toString(),
      value: (currentYear + i).toString(),
    })),
  );

  const term =
    lastRepaymentMonth && lastRepaymentYear
      ? (lastRepaymentYear - currentYear) * 12 + (lastRepaymentMonth - currentMonth)
      : 0;

  const handleAddLoan = () => {
    if (!isInvalid()) {
      setIsClicked(true);
      setTimeout(() => setIsClicked(false), 300);

      addLoan({
        liabilityType,
        currency,
        amount,
        term: term.toString(),
        rate,
        lastRepaymentMonth,
        lastRepaymentYear,
      });
    }
  };

  const changeState = (event, setter) => {
    const { value } = event.target;
    setter(value);
  };

  const liabilityTypes = [
    { value: '', label: 'Select loan type' },
    { value: PERSONAL_LOAN, label: 'Personal loan' },
    { value: VEHICLE_LOAN, label: 'Vehicle loan' },
    { value: MORTGAGE_LOAN, label: 'Mortgage loan' },
    { value: STUDENT_LOAN, label: 'Student loan' },
  ];

  const invalidCurrencies = value => !currencies.map(c => c.value).includes(value);

  const isInvalid = () => {
    return (
      !liabilityType ||
      !currency ||
      invalidCurrencies(currency) ||
      amount <= 0 ||
      term <= 0 ||
      rate < 1 ||
      rate > 100
    );
  };

  return (
    <div className="loan-card">
      <div className="form-container">
        <div className="row-spacer">
          <p className="text-field__input-header">Loan type</p>
          <Select
            id="type-of-loan"
            data-testid="type-of-loan"
            value={liabilityType}
            onChange={e => changeState(e, setLiabilityType)}
            options={liabilityTypes}
          />
        </div>
        <div className="row-spacer">
          <p className="text-field__input-header">Remaining amount</p>
          <CombinedTextField>
            <Select
              id="remaining-amount-currency"
              data-testid="remaining-amount-currency"
              value={currency}
              onChange={e => changeState(e, setCurrency)}
              options={currencies}
            />

            <InputField
              type="text"
              inputMode="numeric"
              pattern="\d*"
              id="remaining-amount"
              placeholder="Remaining amount"
              value={amount}
              onChange={e => updateValue(e, setAmount, MAX_AMOUNT_LIMIT)}
              min={MIN_AMOUNT_LIMIT}
              max={MAX_AMOUNT_LIMIT}
            />
          </CombinedTextField>
        </div>
        <div className="row-spacer">
          <p className="text-field__input-header">Interest rate</p>
          <InputField
            type="text"
            inputMode="numeric"
            pattern="\d*"
            id="interest-rate"
            placeholder="Interest rate (%)"
            min={1}
            max={100}
            value={rate}
            onChange={e =>
              updateValue(e, setRate, MAX_INTEREST_RATE_LIMIT, MIN_INTEREST_RATE_LIMIT)
            }
          />
        </div>
        <div className="row">
          <div className="col">
            Last repayment date
            <div className="row row-padding-top">
              <div className="col col--7/16-large">
                <Select
                  id="last-payment-year"
                  data-testid="last-payment-year"
                  options={years}
                  value={lastRepaymentYear}
                  onChange={e => changeState(e, setLastRepaymentYear)}
                />
              </div>
              <div className="col col--9/16-large">
                <Select
                  id="last-payment-month"
                  data-testid="last-payment-month"
                  options={months}
                  value={lastRepaymentMonth}
                  onChange={e => changeState(e, setLastRepaymentMonth)}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="button-container button-container--spaced">
          <Button
            type="button"
            onClick={cancelLoanInput}
            aria-label="cancel loan"
            className="button button--inverted button--padding cancel-button-active"
          >
            Cancel
          </Button>
          <Button
            type="button"
            isDisabled={isInvalid()}
            onClick={handleAddLoan}
            aria-label="submit loan"
            className={`button--padding ${
              isInvalid() ? 'button-color--disabled' : isClicked ? 'button-clicked' : 'button-color'
            }`}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

LoanInputCard.propTypes = {
  defaultCurrency: PropTypes.string,
  currencies: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  addLoan: PropTypes.func.isRequired,
  cancelLoanInput: PropTypes.func.isRequired,
};

LoanInputCard.defaultProps = {
  defaultCurrency: '',
};

export default LoanInputCard;
